import AddIcon from '@mui/icons-material/Add';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import languages from '../../../helpers/utils/languages.js';
import useTemplate from '../../../hooks/template/useTemplate';
import useTemplateDelete from '../../../hooks/template/useTemplateDelete.js';
import useTemplateUpdate from '../../../hooks/template/useTemplateUpdate.js';
import { LoadingCount } from '../../utils/common/LoadingCount.jsx';
import DeleteComponent from './components/DeleteComponent.jsx';
import { EditComponent } from './components/EditComponent.jsx';
import { FilterSelect } from './components/FilterSelect.jsx';
import OverviewComponent from './components/OverviewComponent.jsx';
import { SearchComponent } from './components/SearchComponent.jsx';
import { TableComponent } from './components/TableComponent.jsx';

const sortOptions = [
  { label: 'Date ↑', field: 'createdAt', value: 1 },
  { label: 'Date ↓', field: 'createdAt', value: -1 },
  { label: 'Modification ↑', field: 'updatedAt', value: 1 },
  { label: 'Modification ↓', field: 'updatedAt', value: -1 },
];

export const DashboardTemplate = () => {
  const serviceList = [
    { code: 'All', label: 'Tous les templates' },
    { code: 'SLO', label: 'SLO' },
    { code: 'SCO', label: 'SCO' },
    { code: 'SAV', label: 'SAV' },
  ];
  const [contentTab, setContentTab] = useState(serviceList[0].code);
  const [formData, setFormData] = useState({
    service: null,
    author: null,
    name: null,
    language: null,
    contactTypes: null,
    sortBy: 'createdAt',
    sortOrder: -1,
  });
  const stores = useSelector((state) => state?.static?.stores);
  const profile = useSelector((state) => state?.login?.profile);
  const { templateList, mutate, isLoading } = useTemplate(formData);
  const [titleSearch, setTitleSearch] = useState([]);
  const [refSearch, setRefSearch] = useState([]);
  const [languageSelect, setLanguageSelect] = useState([]);
  const [storeSelect, setStoreSelect] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState([]);
  const [isView, setIsView] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const navigate = useNavigate();
  const { deleteTemplate } = useTemplateDelete();
  const { updateTemplate } = useTemplateUpdate();

  const contactSelect = [
    { label: 'Clients', id: 'C' },
    { label: 'Professionnels', id: 'P' },
    { label: 'Marketplaces', id: 'M' },
  ];

  const handleAddTemplate = () => {
    navigate('/dashboard-template/create');
  };

  const changeSort = (newValue) => {
    setFormData({
      ...formData,
      sortBy: newValue ? newValue.field : null,
      sortOrder: newValue ? newValue.value : null,
    });
  };

  const handleUpdate = (value) => {
    console.log(
      `Le template ${selectedTemplate.reference} à été mis à jour`,
      value
    );
    updateTemplate(value);
  };

  const handleDelete = () => {
    setIsDelete(false);
    const deleteData = {
      id: selectedTemplate?._id,
      author: profile?._id,
    };
    deleteTemplate(deleteData);
    console.log(`Le template ${selectedTemplate?.reference} à été supprimé`);
    setSelectedTemplate([]);
  };

  useEffect(() => {
    if (contentTab === 'All') {
      setFormData({
        ...formData,
        service: null,
      });
    } else {
      setFormData({
        ...formData,
        service: contentTab,
      });
    }
  }, [contentTab]);

  useEffect(() => {
    mutate();
  }, [formData, isDelete, isEdit, isView]);

  useEffect(() => {
    let titleList = [];
    let refList = [];
    let languageList = [];
    let storeList = [];

    templateList
      ?.filter((item) => item?.reference !== 'DEFAULT-000')
      ?.map((item) => {
        (titleList.push(item.name), refList.push(item.reference));
      });
    setTitleSearch(titleList);
    setRefSearch(refList);
    languages?.map((item) => {
      const display = (
        <Typography>
          <span className={`fi fi-${item?.flag}`} /> - {item?.lang}
        </Typography>
      );
      languageList.push({
        label: item.lang,
        id: item.code2,
        dsiplay: display,
      });
    });
    setLanguageSelect(languageList);
    stores
      ?.filter((item) => item?.shortName !== 'Inconnu')
      ?.map((item) => {
        storeList.push({ label: item.shortName, id: item?._id });
      });
    setStoreSelect(storeList);
  }, [templateList]);

  return (
    <>
      <Box sx={{ padding: 3, fontWeight: 'bold', fontSize: 24 }}>Templates</Box>
      <Box
        sx={{
          m: '0 16px',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Tabs value={contentTab} onChange={(e, value) => setContentTab(value)}>
          {serviceList.map((tab) => (
            <Tab
              key={tab.code}
              value={tab.code}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tab.label}
                  {tab.code === contentTab && (
                    <LoadingCount
                      loading={isLoading}
                      value={` (${
                        templateList?.filter(
                          (item) => item?.reference !== 'DEFAULT-000'
                        )?.length
                      })`}
                    />
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddTemplate}
        >
          Ajouter
        </Button>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          m: 3,
          gap: 2,
        }}
      >
        <SearchComponent
          formData={formData}
          setFormData={setFormData}
          searchRef={refSearch}
          searchTitle={titleSearch}
        />
        <Divider orientation="vertical" flexItem />
        <Autocomplete
          disablePortal
          sx={{ width: '20%' }}
          options={sortOptions}
          getOptionLabel={(option) => `${option.label}`}
          onChange={(e, newValue) => changeSort(newValue)}
          renderInput={(params) => <TextField {...params} label="Trier par" />}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          m: 3,
          gap: 2,
        }}
      >
        <FilterSelect
          props={{ minWidth: '15%' }}
          title="Types de contact"
          options={contactSelect}
          setFormData={setFormData}
          dataField={'contactTypes'}
        />
        <FilterSelect
          props={{ minWidth: '15%' }}
          title="Magasins"
          options={storeSelect}
          setFormData={setFormData}
          dataField={'stores'}
        />
        <FilterSelect
          props={{ minWidth: '15%' }}
          title="Langue"
          options={languageSelect}
          setFormData={setFormData}
          dataField={'language'}
        />
      </Box>
      <Divider variant="middle" sx={{ mt: 1 }} />
      <TableComponent
        templates={templateList?.filter(
          (item) => item?.reference !== 'DEFAULT-000'
        )}
        loading={isLoading}
        setSelectedTemplate={setSelectedTemplate}
        setIsView={setIsView}
        setIsDelete={setIsDelete}
        setIsEdit={setIsEdit}
      />
      <OverviewComponent
        template={selectedTemplate}
        onClose={() => {
          setIsView(false);
          setSelectedTemplate([]);
        }}
        displayed={isView}
      />
      <EditComponent
        template={selectedTemplate}
        onClose={() => {
          setIsEdit(false);
          setSelectedTemplate([]);
        }}
        onSave={(value) => {
          handleUpdate(value);
          setIsEdit(false);
        }}
        displayed={isEdit}
      />
      <DeleteComponent
        template={selectedTemplate}
        onClose={() => setIsDelete(false)}
        onDelete={(_value) => {
          handleDelete();
          setIsDelete(false);
        }}
        displayed={isDelete}
      />
    </>
  );
};
